import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { ErrorBanner } from '@platform/ui';
import { tableTitle } from '../list/header/tableTitle';
import { RecordTabs } from '../tabs/RecordTabs';
import { RecordHeader } from './header/RecordHeader';
import { recordCode } from './header/recordCode';
import type { IRecordLayoutProps } from './IRecordLayoutProps';
import { MiniFormDialog } from './mini/MiniFormDialog';
import { RelationsNavigator } from './navigator/RelationsNavigator';
import { AdvisoryPanel } from './panels/AdvisoryPanel';
import { MappingPanel } from './panels/MappingPanel';
import { SidePanel } from './panels/SidePanel';
import { RecordForm } from './RecordForm';
import { RecordInfoFooter } from './RecordInfoFooter';
import { RecordToolbar } from './toolbar/RecordToolbar';
import { useRecordActions } from './toolbar/useRecordActions';
import { useRecordViewUi } from './useRecordViewUi';

/**
 * Розмітка сторінки запису (прототип v4): «← Модуль», шапка (код, тип, назва, ключовий рядок), панель дій, вкладки з вмістом і
 * «Відомості про запис»; праворуч — «Поради» або «Зіставлення». Чернетку й захист від втрати змін дає обгортка: збережений запис
 * (RecordView) чи новий (NewRecordView — без «Відомостей», з вкладками й діями, що чекають на збереження).
 */
export function RecordLayout({ ws, module, rt, record, tab, variant = 'page', onTab, editor, guard, isNew = false }: IRecordLayoutProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const moduleKey = module.module.key;
  const actions = useRecordActions({ ws, module, rt, record, editor, confirm: guard.confirm, release: guard.release });
  const ui = useRecordViewUi({ module, rt, record, editor, actions, onTab });
  const split = variant === 'split';
  const canEdit = record.permissions?.edit !== false;
  const readOnly = !canEdit || !editor.editing;
  const listLabel = tableTitle(strings.moduleName(module.module.name), strings.entity(rt.key, 'name', rt.name));
  const singular = strings.entity(rt.key, 'singular', rt.singular ?? rt.name).toLocaleLowerCase();
  const base = routes.record(moduleKey, rt.key, record.id);
  return (
    <div className={split ? 'rec__body rec__body--split' : 'rec__body'}>
      <div className="rec__scroll">
        <div className="rec__col">
          {!split && <Link className="rec-back" to={routes.list(moduleKey, rt.key)} title={t('recordPage.backTitle', { module: listLabel })}>← {listLabel}</Link>}
          <RecordHeader ws={ws} moduleKey={moduleKey} listLabel={listLabel} rt={rt} record={record} editor={editor} canEdit={!readOnly} openFull={split && !isNew ? base : undefined} isNew={isNew} />
          <RecordToolbar rt={rt} record={record} editor={editor} actions={actions} canEdit={canEdit} canDelete={record.permissions?.delete !== false} isNew={isNew}
            panel={ui.panel} onPanel={ui.setPanel} onTab={ui.goTab} onNavigator={() => ui.setNavigator(true)} onAddLink={ui.addLink}
            onAddComment={ui.addComment} onAttach={ui.attach} />
          <ErrorBanner error={editor.error ?? ui.uploadError} />
          <RecordTabs ws={ws} module={module} rt={rt} record={record} editor={editor} active={tab} onChange={ui.goTab} isNew={isNew}>
            <RecordForm ws={ws} module={module} rt={rt} record={record} editor={editor} readOnly={readOnly} onLinkModule={ui.openMapping} onOpenLinked={ui.openItem} />
          </RecordTabs>
          {!isNew && <RecordInfoFooter ws={ws} record={record} module={module} rt={rt} editor={editor} canEdit={canEdit} />}
        </div>
      </div>
      {ui.panel === 'advisory' && (
        <SidePanel title={t('recordPage.advisoryPanel.title')} subtitle={t('recordPage.advisoryPanel.subtitle')} onClose={() => ui.setPanel(null)}>
          <AdvisoryPanel rt={rt} record={record} editor={editor} onAction={ui.advisoryAction}
            onAskAi={() => actions.askAi(t('recordPage.adviseAiPrompt', { code: recordCode(record.display, record.code) }))} />
        </SidePanel>
      )}
      {ui.panel === 'mapping' && (
        <SidePanel title={t('recordPage.mappingPanel.title')} subtitle={t('recordPage.mappingPanel.subtitle', { type: singular })} onClose={() => ui.setPanel(null)}>
          <MappingPanel key={ui.mapModule} ws={ws} rt={rt} record={record} initialModule={ui.mapModule} onOpen={ui.openLinked} />
        </SidePanel>
      )}
      {ui.mini && <MiniFormDialog ws={ws} target={ui.mini} onClose={() => ui.setMini(null)} />}
      {ui.navigator && (
        <RelationsNavigator ws={ws} onClose={() => ui.setNavigator(false)}
          center={{ id: record.id, module: moduleKey, recordType: rt.key, display: record.display, title: record.title, status: record.status, level: 0 }} />
      )}
      {guard.dialog}
    </div>
  );
}
